<template>
    <div class="player-mat" :class="{ [position]: position }">
        <div class="player-info">
            <player-label
                :player="player"
                :linkToProfile="true"
				:renderMenu="shouldShowPlayerLabelMenu"
                :theme="gameState.theme"
            />
            <div class="view-cards" @click="onViewCards">
                View Cards <icon class="icon" icon="cards" />
            </div>
        </div>
        <div class="group left">
            <div class="group-nav">
                <span
                    class="group-label selectable"
                    :class="{ ['inactive']: expandedGroup !== typeSupply}"
                    @click="onSelectUnitGroup(typeSupply)"
                >
                    <span class="group-label-text">
                        {{typeSupply}}

                        <bishop-toggle
                            :show="position === 'bottom' && selectedBishopInHand"
                            :onClickCallback="toggleRecruitType"
                            :enabled="bishopRecruit"
                        />
                    </span>
                </span>
                <span
                    class="group-label selectable"
                    :class="{ ['inactive']: expandedGroup !== typeEliminated}"
                    @click="onSelectUnitGroup(typeEliminated)"
                >
                    <span class="group-label-text">
                        {{typeEliminated}}
                    </span>
                </span>
                <span
                    class="group-label selectable"
                    :class="{ ['inactive']: expandedGroup !== typeReserve}"
                    @click="onSelectUnitGroup(typeReserve)"
                    v-if="showReserve"
                >
                    <span class="group-label-text">
                        {{typeReserve}}
                    </span>
                </span>
            </div>
            <div class="group-content" v-if="expandedGroup === typeSupply" >
                <unit-stack
                    v-for="(unitStack, index) of groupUnitsByType(unitsInSupply(player))"
                    :key="`supply-unit-stack-${index}`"
                    :units="unitStack"
                />
            </div>
            <div class="group-content" v-if="expandedGroup === typeEliminated" >
                <unit-stack
                    v-for="(unitStack, index) of groupUnitsByType(eliminatedUnits(player))"
                    :key="`supply-unit-stack-${index}`"
                    :units="unitStack"
                />
            </div>
            <div class="group-content reserve" v-if="expandedGroup === typeReserve">
                <div class="unit-stack" v-if="infiltratorDecoyCoin">
                    <unit-coin
                        :unit="infiltratorDecoyCoin"
                        :showActions="false"
                        index="infiltrator-decoy-coin"
                    />
                </div>
                <div class="unit-stack" v-if="skirmisherDecoyCoin">
                    <unit-coin
                        :unit="skirmisherDecoyCoin"
                        :showActions="false"
                        index="infiltrator-decoy-coin"
                    />
                </div>
                <div class="unit-stack" v-if="assassinPoisonTokens.length">
                    <div class="unit-coin">
                        <unit-image
                            classes="reserve-unit-image"
                            :unitType="UNIT_TYPE_ASSASSIN"
                        />
                    </div>
                    <img
                        class="poison-token"
                        :src="getAssetUrl(`@/assets/poison_token_${UNIT_TYPE_ASSASSIN.toLowerCase()}.png`)"
                        v-for="(token, index) in assassinPoisonTokens" :key="`assassin-poison-token-${index}`"
                    >
                </div>
                <div class="unit-stack" v-if="saboteurPoisonTokens.length">
                    <div class="unit-coin">
                        <unit-image
                            classes="reserve-unit-image"
                            :unitType="UNIT_TYPE_SABOTEUR"
                        />
                    </div>
                    <img
                        class="poison-token"
                        :src="getAssetUrl(`@/assets/poison_token_${UNIT_TYPE_SABOTEUR.toLowerCase()}.png`)"
                        v-for="(_token, index) in saboteurPoisonTokens" :key="`saboteur-poison-token-${index}`"
                    >
                </div>
            </div>
        </div>
        <div class="group middle">
            <div class="group-nav">
                <span class="group-label">
                    <span class="group-label-text">{{typeHand}}</span>
                </span>
            </div>
            <div class="group-content"  v-if="['top', 'bottom'].includes(position)">
                <unit-stack
                    v-for="(unit, index) of unitsInHand"
                    :key="`unit-in-hand-${index}`"
                    :units="[unit]"
                />
            </div>
            <div class="group-content"  v-if="['left', 'right'].includes(position)">
                <unit-stack :units="unitsInHand" />
            </div>
        </div>
        <div class="group bag-wrapper" :class="{['has-top-of-bag-units']: topOfBagUnits(player).length}">
            <div class="bag-content">
                <img class="bag" src="../../assets/bag.png" />
                <span class="quantity-in-bag" v-if="showHand">{{ unitsInBag.length }}</span>
            </div>
            <div class="top-of-bag-units-wrapper" v-if="topOfBagUnits(player).length">
                <unit-stack
                    :type="UNIT_STATE_TOP_OF_BAG"
                    :units="topOfBagUnits(player)"
                />
            </div>
        </div>
        <div class="group right">
            <div class="group-nav">
                <span class="group-label">
                    <span class="group-label-text">
                        {{typeDiscard}}
                    </span>
                </span>
            </div>
            <div class="group-content" :class="{[`unit-count-${groupUnitsByType(discardedUnits(player)).length}`]: true}">
                <unit-stack
                    v-for="(unitStack, index) of groupUnitsByType(discardedUnits(player))"
                    :key="`discarded-unit-stack-${index}`"
                    :actions="actionsState.actions"
                    :units="unitStack"
                    :revealFacedown="showHand"
                />
            </div>
        </div>
        <unit-types-modal
            :onCloseCallback="dismissUnitTypesModal"
            :show="showUnitTypesModal"
            :unitTypes="unitTypes"
        />
    </div>
</template>

<script>
import { getAssetUrl } from '../../helpers/assets';

import {
    DECREE_TYPE_ENLIST,
    DECREE_TYPE_REINFORCE,
} from '../DecreeCard/DecreeCard-types';
import gameState from '../Game/Game-state';
import {
    UNIT_STATE_IN_SUPPLY,
    UNIT_STATE_RECRUITED,
    UNIT_STATE_DISCARDED_FACEUP,
    UNIT_STATE_DISCARDED_FACEDOWN,
    UNIT_STATE_ELIMINATED,
    UNIT_STATE_IN_RESERVE,
    UNIT_STATE_TOP_OF_BAG,
    UNIT_TYPE_BISHOP,
    UNIT_TYPE_ASSASSIN,
    UNIT_TYPE_SABOTEUR,
    UNIT_TYPE_INFILTRATOR,
    UNIT_TYPE_SKIRMISHER,
    UNIT_TYPE_DECOY_COIN_INFILTRATOR,
    UNIT_TYPE_DECOY_COIN_SKIRMISHER,
} from '../Unit/Unit-types';

import {
    UNIT_GROUP_SUPPLY,
    UNIT_GROUP_HAND,
    UNIT_GROUP_DISCARD,
    UNIT_GROUP_ELIMINATED,
    UNIT_GROUP_RESERVE,
} from '../UnitGroup/UnitGroup-types';
import { API_BASE_URL } from '@/helpers/env';

export default {
    name: 'PlayerMat',
    props: [
        'gameState',
        'player',
        'showHand',
        'position',
        'unitsInHand',
        'unitsInBag',
    ],
    data() {
        return {
            discardedStates: [
                UNIT_STATE_RECRUITED,
                UNIT_STATE_DISCARDED_FACEDOWN,
                UNIT_STATE_DISCARDED_FACEUP,
            ],
            typeSupply: UNIT_GROUP_SUPPLY,
            typeHand: UNIT_GROUP_HAND,
            typeDiscard: UNIT_GROUP_DISCARD,
            typeEliminated: UNIT_GROUP_ELIMINATED,
            typeReserve: UNIT_GROUP_RESERVE,
            expandedGroup: UNIT_GROUP_SUPPLY,
            showUnitTypesModal: false,
            ['UNIT_TYPE_ASSASSIN']: UNIT_TYPE_ASSASSIN,
            ['UNIT_TYPE_SABOTEUR']: UNIT_TYPE_SABOTEUR,
            ['UNIT_STATE_TOP_OF_BAG']: UNIT_STATE_TOP_OF_BAG,
        };
    },
    watch: {
        activeDecree(activeDecree, prevActiveDecree) {
            if (!prevActiveDecree && activeDecree === DECREE_TYPE_REINFORCE
                && this.expandedGroup !== UNIT_GROUP_ELIMINATED
                && this.position === 'bottom') {
                this.expandedGroup = UNIT_GROUP_ELIMINATED;
            }

            if (prevActiveDecree === DECREE_TYPE_REINFORCE && !activeDecree
                && this.expandedGroup !== UNIT_GROUP_SUPPLY
                && this.position === 'bottom') {
                this.expandedGroup = UNIT_GROUP_SUPPLY;
            }

            if (!prevActiveDecree && activeDecree === DECREE_TYPE_ENLIST
                && this.expandedGroup !== UNIT_GROUP_SUPPLY
                && this.position === 'bottom') {
                this.expandedGroup = UNIT_GROUP_SUPPLY;
            }
        },
    },
    computed: {
        shouldShowPlayerLabelMenu() {
            return this.player?.user?.id !== this.userId;
        },
        userId() {
            return this.$store.state.loginState.userId;
        },
        activeDecree() {
            return this.$store.state.actionsState.activeDecree;
        },
        actionsState() {
            return this.$store.state.actionsState;
        },
        activePlayer() {
            return this.player.id === this.gameState.activePlayerId;
        },
        playerUnits() {
            return this.gameState.players.find(p => p.id === this.player.id)?.units ?? [];
        },
        facedownUnits() {
            return this.gameState.unitsInBag.facedownUnits;
        },
        unitTypes() {
            return this.gameState.unitTypes.filter(unit => unit.playerId === this.player.id);
        },
        isVertical() {
            return ['left', 'right'].includes(this.position);
        },
        selectedBishopInHand() {
            return this.gameState.unitsInHand.filter(unit =>
                unit.type === UNIT_TYPE_BISHOP &&
                unit.id === this.actionsState.selectedUnitId,
            )[0];
        },
        apiUrl() {
            return API_BASE_URL;
        },
        imgBaseUrl() {
            return `${this.apiUrl}/assets`;
        },
        bishopRecruit() {
            return this.actionsState.bishopRecruit;
        },
        showReserve() {
            return this.gameState.unitTypes.filter(unit =>
                unit.playerId === this.player.id
                && [
                    UNIT_TYPE_ASSASSIN,
                    UNIT_TYPE_SABOTEUR,
                    UNIT_TYPE_INFILTRATOR,
                    UNIT_TYPE_SKIRMISHER,
                ].includes(unit.type),
            ).length;
        },
        assassinPoisonTokens() {
            const hasAssassin = this.unitTypes.find(unit => unit.type === UNIT_TYPE_ASSASSIN);
            return this.gameState.poisonTokens?.filter(pt =>
                pt.ownerUnitTypeId === hasAssassin?.id && !pt.assignedUnitId
            ) ?? [];
        },
        saboteurPoisonTokens() {
            const hasSaboteur = this.unitTypes.find(unit => unit.type === UNIT_TYPE_SABOTEUR);
            return this.gameState.poisonTokens?.filter(pt =>
                pt.ownerUnitTypeId === hasSaboteur?.id && !pt.assignedUnitId
            ) ?? [];
        },
        infiltratorDecoyCoin() {
            return this.playerUnits.find(unit =>
                unit.type === UNIT_TYPE_DECOY_COIN_INFILTRATOR &&
                unit.state === UNIT_STATE_IN_RESERVE,
            );
        },
        skirmisherDecoyCoin() {
            return this.playerUnits.find(unit =>
                unit.type === UNIT_TYPE_DECOY_COIN_SKIRMISHER &&
                unit.state === UNIT_STATE_IN_RESERVE,
            );
        },
    },
    methods: {
		getAssetUrl,
        discardedUnits(player) {
            return player.units.filter(unit => this.discardedStates.includes(unit.state));
        },
        dismissUnitTypesModal() {
            this.showUnitTypesModal = false;
        },
        groupUnitsByType(units = []) {
            const unitsByType = {};
            const groupedUnits = [];
			const sortedUnits = units.sort((unitA, unitB) => unitA.id - unitB.id);

            for (const unit of sortedUnits) {
                if (unitsByType[unit.type]) {
                    unitsByType[unit.type].push(unit);
                } else {
                    unitsByType[unit.type] = [unit];
                }
            }

            for (const [_, unitGroup] of Object.entries(unitsByType)) {
                groupedUnits.push(unitGroup);
            }

            return groupedUnits;
        },
        eliminatedUnits(player) {
            return player.units.filter(unit => unit.state === UNIT_STATE_ELIMINATED);
        },
        topOfBagUnits(player) {
            return player.units.filter(unit => unit.state === UNIT_STATE_TOP_OF_BAG);
        },
        onSelectUnitGroup(unitGroup) {
            const {
                activeDecree,
                expandedGroup,
                typeEliminated,
                typeSupply,
            } = this;

            if (activeDecree === DECREE_TYPE_REINFORCE && expandedGroup === typeEliminated) {
                return;
            }

            if (activeDecree === DECREE_TYPE_ENLIST && expandedGroup === typeSupply) {
                return;
            }

            this.expandedGroup = unitGroup;
        },
        onViewCards() {
            this.showUnitTypesModal = true;
        },
        toggleRecruitType() {
            const poisonedBishop = this.actionsState.selectedUnitActions.find(action => action.type === 'cure_poison');

            if (poisonedBishop) {
                this.$notify({
                    group: 'app',
                    title: '',
                    type: 'error',
                    text: 'You cannot use the Bishop\'s tactic when it\'s poisoned',
                });
                return;
            }

            this.$store.dispatch('setBishopRecruit', { value: !this.bishopRecruit, notify: true });
        },
        unitsInSupply(player) {
            return player.units.filter(unit => unit.state === UNIT_STATE_IN_SUPPLY);
        },
    },
};
</script>

<style scoped lang="scss">
    @use "@/styles/styles.scss" as *;

    .player-mat {
        display: flex;
        flex-grow: 1;
        justify-content: center;
        position: relative;

        &.left,
        &.right {
            flex-direction: column;

            .group {
                width: 100%;

                .group-nav {
                    margin-top: 10px;
                }

                .group-content {
                    flex-direction: column;

                    .unit-coin,
                    .unit-stack {
                        align-self: center;
                        margin: 0;

                        &.in-hand {
                            box-shadow: none;
                        }
                    }

                    .unit-stack {
                        width: 35%;
                    }
                }

                .bag-content {
                    margin-top: 10px;

                    .bag {
                        margin-left: 10px;
                        max-width: 45px;
                    }
                }
            }
        }

        .group {
            display: flex;
            flex-direction: column;
            flex-grow: 1;
            margin: 0 2%;
            min-width: 25%;

            &.bag-wrapper {
                align-self: flex-end;
                min-width: 7%;
                position: relative;

                &.has-top-of-bag-units {
                    .bag-content {

                        .quantity-in-bag {
                            top: -15%;
                            color: black;
                        }
                    }
                }

                .bag-content {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    position: relative;

                    .bag {
                        align-self: center;
                        height: auto;
                        max-height: 90%;
                        width: 90%;
                    }

                    .quantity-in-bag {
                        color: $silver;
                        font-size: 1em;
                        font-family: 'Livingstone';
                        left: calc(50% - 2px);
                        position: absolute;
                        top: 60%;
                        transform: translate(-50%, -50%);
                    }
                }
            }

            .group-nav {
                display: flex;
                justify-content: center;
                margin-bottom: 4%;

                .group-label {
                    display: inline;
                    font-family: 'Livingstone';
                    font-size: .9em;
                    position: relative;
                    user-select: none;

                    &.selectable {
                        cursor: pointer;
                        user-select: none;
                    }

                    &.inactive {
                        opacity: .3;
                    }

                    &:not(:first-of-type) {
                        margin-left: 5%;
                    }
                }
            }

            .group-content {
                display: flex;
                justify-content: center;

                &.reserve {

                    .unit-coin {
                        border: 2.5px outset transparent;

                        .reserve-unit-image {
                            display: block;
                            max-height: 100%;
                            max-width: 100%;
                        }
                    }
                }

                .unit-stack {
                    margin: 0 1%;
                    width: 25%;
                    $zIndex: 10;

                    @for $i from 1 through 10 {
                        &:nth-of-type(#{$i}) {
                            z-index: #{$zIndex - $i};
                        }
                    }

                    .poison-token {
                        max-width: 33%;
                        position: absolute;
                        border: 1px solid $dark-gray-alt;
                        border-radius: 50%;

                        &:nth-of-type(1) {
                            left: 15%;
                            bottom: 10%;
                            transform: translateX(-50%);
                        }

                        &:nth-of-type(2) {
                            left: 50%;
                            bottom: -10%;
                            transform: translateX(-50%);
                        }

                        &:nth-of-type(3) {
                            left: 85%;
                            bottom: 10%;
                            transform: translateX(-50%);
                        }
                    }
                }

                &.unit-count-5 {
                    .unit-stack {
                        width: 20%;
                    }
                }

                &.unit-count-6 {
                    .unit-stack {
                        width: 16.6666666667%;
                    }
                }
            }

            &.middle {

                .group-content {

                    .unit-stack {
                        z-index: 1;
                    }
                }
            }
        }

        .player-info {
            align-self: center;
        }

        .view-cards {
            cursor: pointer;
            display: flex;
            justify-content: center;
            margin-top: 5px;
            font-size: .8em;

            > * {
                align-self: center;
            }

            .icon {
                height: auto;
                vertical-align: middle;
                width: 18px;
            }
        }

        .units-modal {
            background: $cream;

            .modal-content {
                display: flex;
                width: auto;
                max-width: none;
                padding: 30px 15px;
                background: $cream;

                .unit-card {
                    transform: scale(1.1);

                    &:not(:last-of-type) {
                        margin-right: 50px;
                    }
                }
            }

            .dismiss-btn {
                cursor: pointer;
                font-size: 1.6em;
                position: absolute;
                right: 15px;
                top: 10px;
            }
        }

        .top-of-bag-units-wrapper {
            position: absolute;
            width: 60%;
            top: 75%;
            left: 50%;
            transform: translate(-55%, -50%);
        }
    }

    @media only screen and (min-width: $screen-md) {

        .player-mat {

            &.left,
            &.right {

                .group {

                    .bag-content {

                        .bag {
                            max-width: 54px;
                        }
                    }
                }
            }
        }

    }

</style>
